import { defineStore } from "pinia";
import { onMounted, ref } from "vue";
import { getBuildingProjectEnums } from "@/api/services/BuildingProject";
import handleApiError from "@/services/Utils/ErrorHandler";

interface BuildingProjectEnums {
  buildingProjectStageEnums: string[];
  buildingTypeEnum: string[];
  constructionElectricitySupplyEnum: string[];
  constructionHeatSupplyEnum: string[];
  operationElectricitySupplyEnum: string[];
  operationHeatSupplyEnum: string[];
  defaultBranches: string[];
  materialSourceEnum: string[];
}

interface BuildingProjectEnumsProvider {
  getBuildingProjectStageEnums: () => Promise<string[]>;
  getBuildingTypeEnum: () => Promise<string[]>;
  getConstructionElectricitySupplyEnum: () => Promise<string[]>;
  getConstructionHeatSupplyEnum: () => Promise<string[]>;
  getOperationElectricitySupplyEnum: () => Promise<string[]>;
  getOperationHeatSupplyEnum: () => Promise<string[]>;
  getDefaultBranches: () => Promise<string[]>;
  getMaterialSourceEnum: () => Promise<string[]>;
  getBuildingProjectEnumsObject: () => Promise<BuildingProjectEnums | undefined>;
}

const useBuildingProjectEnums = defineStore("BuildingProjectsEnumsProvider", (): BuildingProjectEnumsProvider => {
  // Helpers
  const isPopulated = ref<boolean>(false);

  // States
  const buildingProjectEnums = ref<BuildingProjectEnums | undefined>();

  // Initialization task
  const initializeBuildingProjectEnums = (async () => {
    try {
      buildingProjectEnums.value = await getBuildingProjectEnums();
      return 200;
    } catch (error) {
      handleApiError(error);
      throw 500;
    }
  })();

  // Initialize
  onMounted(async () => {
    if (isPopulated.value) return;
    try {
      await initializeBuildingProjectEnums;
      isPopulated.value = true;
    } catch (error) {
      handleApiError(error);
    }
  });

  // Getters
  const getBuildingProjectStageEnums = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.buildingProjectStageEnums || [];
  };

  const getBuildingTypeEnum = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.buildingTypeEnum || [];
  };

  const getConstructionElectricitySupplyEnum = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.constructionElectricitySupplyEnum || [];
  };

  const getConstructionHeatSupplyEnum = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.constructionHeatSupplyEnum || [];
  };

  const getOperationElectricitySupplyEnum = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.operationElectricitySupplyEnum || [];
  };

  const getOperationHeatSupplyEnum = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.operationHeatSupplyEnum || [];
  };

  const getDefaultBranches = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.defaultBranches || [];
  };

  const getMaterialSourceEnum = async () => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }
    return buildingProjectEnums.value?.materialSourceEnum || [];
  };

  const getBuildingProjectEnumsObject = async (): Promise<BuildingProjectEnums | undefined> => {
    if (!buildingProjectEnums.value) {
      await initializeBuildingProjectEnums;
    }

    return buildingProjectEnums.value;
  };

  return {
    getBuildingProjectStageEnums,
    getBuildingTypeEnum,
    getConstructionElectricitySupplyEnum,
    getConstructionHeatSupplyEnum,
    getOperationElectricitySupplyEnum,
    getOperationHeatSupplyEnum,
    getDefaultBranches,
    getMaterialSourceEnum,
    getBuildingProjectEnumsObject,
  };
});

export default useBuildingProjectEnums;
